import React, { useEffect, useRef, useState } from 'react';
import type { WidgetConfig } from './types';

const EXPANDED_STORAGE_KEY = 'atlas-widget-modal-expanded';

function getStoredExpanded(): boolean {
    try {
        const stored = localStorage.getItem(EXPANDED_STORAGE_KEY);
        return stored === 'true';
    } catch {
        return false;
    }
}

function setStoredExpanded(expanded: boolean): void {
    try {
        localStorage.setItem(EXPANDED_STORAGE_KEY, String(expanded));
    } catch {
        // ignore
    }
}

interface WidgetModalProps {
    isOpen: boolean;
    onClose: () => void;
    overlayPosition: WidgetConfig['overlayPosition'];
    children: React.ReactNode;
}

/**
 * Modal component that contains the Atlas chat interface
 * Slides in from the configured corner with animation
 */
export const WidgetModal: React.FC<WidgetModalProps> = ({
    isOpen,
    onClose,
    overlayPosition,
    children,
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);
    const [isExpanded, setIsExpanded] = useState(getStoredExpanded);

    // Handle escape key to close modal
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            // Store currently focused element and focus modal
            previousActiveElement.current = document.activeElement as HTMLElement;
            modalRef.current?.focus();
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            // Restore focus when modal closes
            if (!isOpen && previousActiveElement.current) {
                previousActiveElement.current.focus();
            }
        };
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const positionClasses = getPositionClass(overlayPosition);

    return (
        <div
            className="atlas-widget-modal-overlay"
            onClick={onClose}
            style={{ display: isOpen ? 'flex' : 'none' }}
        >
            <div
                ref={modalRef}
                className={`atlas-widget-modal ${positionClasses}${isExpanded ? ' atlas-widget-modal--expanded' : ''}`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="FleetGO AI Assistant"
                tabIndex={-1}
            >
                {/* Modal Header */}
                <div className="atlas-widget-modal__header">
                    <div className="atlas-widget-modal__title">
                        <img
                            src={getBotImageUrl()}
                            alt=""
                            className="atlas-widget-modal__logo"
                            width={28}
                            height={28}
                        />
                        <span>FleetGO AI Assistant</span>
                    </div>
                    <div className="atlas-widget-modal__header-actions">
                        <button
                            type="button"
                            className="atlas-widget-modal__expand"
                            onClick={() => {
                            const next = !isExpanded;
                            setIsExpanded(next);
                            setStoredExpanded(next);
                        }}
                            aria-label={isExpanded ? 'Restore FleetGO AI Assistant' : 'Expand FleetGO AI Assistant'}
                        >
                            {isExpanded ? (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 8V4h4M16 8V4h-4M16 12v4h-4M4 12v4h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 4h4v4M16 4h-4v4M16 16h-4v-4M4 16h4v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </button>
                        <button
                            type="button"
                            className="atlas-widget-modal__close"
                            onClick={onClose}
                            aria-label="Close FleetGO AI Assistant"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Modal Content */}
                <div className="atlas-widget-modal__content">
                    {children}
                </div>
            </div>
        </div>
    );
};

/**
 * Get the bot image URL
 */
function getBotImageUrl(): string {
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
        const src = scripts[i].src;
        if (src && src.includes('atlas-widget')) {
            const baseUrl = src.substring(0, src.lastIndexOf('/'));
            return `${baseUrl}/bot.png`;
        }
    }
    return '/bot.png';
}

/**
 * Get CSS class based on overlay position for slide-in animation direction
 */
function getPositionClass(overlayPosition: WidgetConfig['overlayPosition']): string {
    switch (overlayPosition) {
        case 'bottom-right':
            return 'atlas-widget-modal--bottom-right';
        case 'bottom-left':
            return 'atlas-widget-modal--bottom-left';
        case 'top-right':
            return 'atlas-widget-modal--top-right';
        case 'top-left':
            return 'atlas-widget-modal--top-left';
        case 'left':
            return 'atlas-widget-modal--left';
        case 'right':
            return 'atlas-widget-modal--right';
        default:
            return 'atlas-widget-modal--bottom-right';
    }
}
